/**
 * The holder raffle draw, run in the visitor's browser so the page does not have to be
 * trusted. It is the same method the CMS runs (back-end src/lib/raffle/draw.ts), so a change
 * here has to be made there too.
 *
 * Tickets are ordered by wallet, then token id. Wallets with a reserved prize hold none.
 * Draw k picks sha256(`${seed}:${k}`) mod (tickets left), and the winner's other tickets
 * leave the pool, so each wallet wins once.
 */

export type TokenId = number | string;

export interface RaffleHolder {
  address: string;
  ens: string | null;
  tokens: TokenId[];
}

export interface RaffleSnapshot {
  collection: string;
  contract: string;
  chain: string;
  snapshotBlock: number;
  snapshotTime: string;
  totalSupply: number;
  holders: RaffleHolder[];
}

export type TicketRule = 'token' | 'wallet';

export interface RaffleTicket {
  address: string;
  tokenId: TokenId;
}

export interface RaffleDraw {
  k: number;
  hash: string;
  poolSize: number;
  index: number;
  ticket: RaffleTicket;
}

/** A raffle as the CMS returns it (collection `raffles`, depth 0). */
export interface Raffle {
  id: number;
  title: string;
  prizes?: Array<{ id?: string | null; title: string; quantity?: number | null; description?: string | null }> | null;
  reserved?: Array<{ id?: string | null; wallet: string; prize: string; note?: string | null }> | null;
  ticketRule?: TicketRule | null;
  drawTime?: string | null;
  drawTimeSetAt?: string | null;
  extraRules?: string | null;
  claimLabel?: string | null;
  claimUrl?: string | null;
  verifyInstructions?: string | null;
  status: 'draft' | 'snapshot' | 'drawn';
  snapshotBlock?: number | null;
  snapshotTime?: string | null;
  snapshotSha256?: string | null;
  snapshotJson?: string | null;
  drawBlock?: number | null;
  drawBlockTime?: string | null;
  drawBlockHash?: string | null;
}

export async function sha256hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
}

const byTokenId = (a: TokenId, b: TokenId) => {
  const x = BigInt(a);
  const y = BigInt(b);
  return x < y ? -1 : x > y ? 1 : 0;
};

/** One ticket per piece, or one per wallet (its lowest token id). */
export function raffleTickets(
  snapshot: RaffleSnapshot,
  reserved: string[] = [],
  rule: TicketRule = 'token'
): RaffleTicket[] {
  const skip = new Set(reserved.map(address => address.toLowerCase()));
  return [...snapshot.holders]
    .filter(holder => !skip.has(holder.address.toLowerCase()))
    .sort((a, b) => (a.address < b.address ? -1 : 1))
    .flatMap(holder => {
      const tokens = [...holder.tokens].sort(byTokenId);
      return (rule === 'wallet' ? tokens.slice(0, 1) : tokens).map(tokenId => ({ address: holder.address, tokenId }));
    });
}

export async function runRaffle(seed: string, pool: RaffleTicket[], count: number): Promise<RaffleDraw[]> {
  let left = pool.slice();
  const draws: RaffleDraw[] = [];
  for (let k = 1; k <= count && left.length; k++) {
    const hash = await sha256hex(`${seed}:${k}`);
    const index = Number(BigInt('0x' + hash) % BigInt(left.length));
    const ticket = left[index];
    draws.push({ k, hash, poolSize: left.length, index, ticket });
    left = left.filter(t => t.address !== ticket.address);
  }
  return draws;
}

/** The prize each draw wins: prizes in the order given, each repeated by its quantity. */
export function prizeForEachDraw(prizes: Array<{ title: string; quantity?: number | null }>): string[] {
  return prizes.flatMap(prize =>
    Array.from({ length: Math.max(0, Math.floor(prize.quantity ?? 1)) }, () => prize.title)
  );
}

const CHAINS: Record<string, { explorer: string; nodes: string[] }> = {
  ethereum: {
    explorer: 'https://etherscan.io',
    nodes: ['https://ethereum-rpc.publicnode.com', 'https://eth.drpc.org', 'https://rpc.flashbots.net'],
  },
  base: { explorer: 'https://basescan.org', nodes: ['https://base-rpc.publicnode.com', 'https://mainnet.base.org'] },
  polygon: {
    explorer: 'https://polygonscan.com',
    nodes: ['https://polygon-bor-rpc.publicnode.com', 'https://polygon-rpc.com'],
  },
};

export const explorerFor = (chain: string) => (CHAINS[chain] ?? CHAINS.ethereum).explorer;

/** Asks public nodes for the draw block, so the page does not just trust the hash it was given. */
export async function fetchBlockHash(chain: string, block: number): Promise<string | null> {
  for (const url of (CHAINS[chain] ?? CHAINS.ethereum).nodes) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'eth_getBlockByNumber',
          params: ['0x' + block.toString(16), false],
        }),
      });
      const json = await res.json();
      if (json.result?.hash) return String(json.result.hash).toLowerCase();
    } catch {
      /* try the next node */
    }
  }
  return null;
}
